<template>
  <Layout title="My Vouchers" description="View all vouchers linked to your jobs">
    <div class="container mx-auto p-4">

      <!-- Header -->
      <div class="flex justify-end mb-4">
        <button
          class="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap"
          @click="goCreateVoucher"
        >
          + Create Voucher
        </button>
      </div>

      <!-- Filters -->
      <div class="flex flex-row gap-3 items-end flex-wrap border rounded-lg bg-white p-4 mb-6">
        <label class="flex flex-col text-xs font-medium text-gray-600 gap-1">
          Start Date
          <input
            v-model="filters.startDate"
            type="date"
            :max="filters.endDate || undefined"
            class="border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900"
          />
        </label>

        <label class="flex flex-col text-xs font-medium text-gray-600 gap-1">
          End Date
          <input
            v-model="filters.endDate"
            type="date"
            :min="filters.startDate || undefined"
            class="border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900"
          />
        </label>

        <label class="flex flex-col text-xs font-medium text-gray-600 gap-1">
          Vessel
          <select
            v-model="filters.vessel"
            class="border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 min-w-[12rem]"
          >
            <option value="">All vessels</option>
            <option v-for="name in vesselOptions" :key="name" :value="name">
              {{ name }}
            </option>
          </select>
        </label>

        <label v-if="isAdmin" class="flex flex-col text-xs font-medium text-gray-600 gap-1">
          Driver
          <select
            v-model="filters.driverId"
            class="border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-900 min-w-[12rem]"
          >
            <option value="">All drivers</option>
            <option v-for="d in driverOptions" :key="d.id" :value="d.id">
              {{ d.name }}
            </option>
          </select>
        </label>

        <div class="flex gap-2">
          <button
            class="bg-gray-800 hover:bg-gray-900 text-white px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap"
            @click="fetchVouchers()"
          >
            Search
          </button>

          <button
            v-if="hasActiveFilters"
            class="bg-gray-300 hover:bg-gray-400 text-gray-800 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap"
            @click="clearFilters"
          >
            Clear
          </button>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="isLoading && !vouchers.length" class="text-center text-gray-500">
        Loading vouchers...
      </div>

      <!-- Voucher List -->
      <div v-else-if="vouchers.length" class="space-y-4">
        <div
          v-for="(v, idx) in vouchers"
          :key="v.voucherId || idx"
          class="flex flex-col sm:flex-row justify-between items-start sm:items-center border rounded-lg shadow-sm bg-white p-4 gap-3"
        >
          <!-- Left -->
          <div>
            <h2 class="text-lg font-semibold text-gray-900">
              Transport Voucher - {{ formatDT(v.createdAt) }}
            </h2>

            <p class="text-sm text-gray-600">
              {{ v.voucherId }}
            </p>

            <div class="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-gray-700">
              <span>
                <span class="font-medium text-gray-500">Vessel:</span>
                {{ v.vessel || "—" }}
              </span>

              <span>
                <span class="font-medium text-gray-500">Driver:</span>
                {{ v.driver || "—" }}
              </span>
            </div>
          </div>

          <!-- Right -->
          <div class="flex gap-2">
            <router-link
              :to="isAdmin ? `/AdminVoucher/${v.voucherId}` : `/DriverVoucher/${v.voucherId}`"
              class="bg-green-600 hover:bg-green-700 text-white text-sm font-semibold py-2 px-4 rounded-lg whitespace-nowrap"
            >
              View Voucher
            </router-link>

            <button
              class="bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold py-2 px-4 rounded-lg whitespace-nowrap"
              @click="openLinkModal(v.voucherId)"
            >
              Link to Job
            </button>
          </div>
        </div>

      </div>

      <!-- Empty -->
      <div v-else class="text-center text-gray-500">
        <span v-if="hasAppliedFilters">
          No vouchers match these filters{{ hasMore ? " yet" : "" }}.
        </span>

        <span v-else>
          No vouchers found.
        </span>
      </div>

      <!-- Filtered searches stop after a scan limit, so "Load More" can
           still find matches even when this page came back empty -->
      <div v-if="showLoadMore" class="text-center">
        <button
          class="mt-4 bg-gray-800 text-white px-4 py-2 rounded-lg disabled:opacity-60"
          :disabled="isLoading"
          @click="fetchVouchers(true)"
        >
          {{ isLoading ? "Loading..." : vouchers.length ? "Load More" : "Search Older Vouchers" }}
        </button>
      </div>

      <!-- Link Job Modal -->
      <div
        v-if="showLinkModal"
        class="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50"
      >
        <div class="bg-white rounded-lg shadow-lg w-full max-w-md p-6">
          <h3 class="text-lg font-semibold mb-4">
            Link Voucher to Job
          </h3>

          <input
            v-model="linkJobId"
            type="text"
            placeholder="Enter Job ID"
            class="w-full border rounded px-3 py-2 mb-4"
          />

          <div class="flex justify-end gap-2">
            <button
              class="px-4 py-2 bg-gray-300 rounded"
              @click="closeLinkModal"
            >
              Cancel
            </button>

            <button
              class="px-4 py-2 bg-indigo-600 text-white rounded"
              @click="confirmLinkJob"
            >
              Link
            </button>
          </div>
        </div>
      </div>
    </div>
  </Layout>
</template>

<script>
import Layout from "@/components/Layout.vue";
import axios from "axios";
import { useToast } from "vue-toastification";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

const emptyFilters = () => ({
  startDate: "",
  endDate: "",
  vessel: "",
  driverId: "",
});

const hasAnyFilter = (f) =>
  Boolean(f.startDate || f.endDate || f.vessel || f.driverId);

export default {
  name: "VoucherList",

  components: {
    Layout,
  },

  data() {
    return {
      vouchers: [],
      isLoading: true,

      selectedDate: "",

      // Form values, and the values the current list was searched with
      // ("Load More" keeps using the latter until Search is pressed again)
      filters: emptyFilters(),
      appliedFilters: emptyFilters(),
      vesselOptions: [],
      driverOptions: [],

      userId: "",

      showLinkModal: false,
      selectedVoucherId: "",
      linkJobId: "",

      lastDocId: null,
      hasMore: true,
      pageSize: 10,
    };
  },

  setup() {
    const toast = useToast();

    return {
      toast,
    };
  },

  computed: {
    isAdmin() {
      return this.$store.state.systemAccess?.admin === true;
    },

    hasActiveFilters() {
      return hasAnyFilter(this.filters) || hasAnyFilter(this.appliedFilters);
    },

    hasAppliedFilters() {
      return hasAnyFilter(this.appliedFilters);
    },

    showLoadMore() {
      if (!this.hasMore || (this.isLoading && !this.vouchers.length)) {
        return false;
      }

      return this.vouchers.length > 0 || this.hasAppliedFilters;
    },
  },

  methods: {
    async getAuthHeader() {
      const auth = getAuth();
      const user = auth.currentUser;

      if (!user) {
        throw new Error("Not logged in");
      }

      const token = await user.getIdToken(true);

      return {
        Authorization: token,
      };
    },

    clearFilters() {
      this.filters = emptyFilters();
      this.fetchVouchers();
    },

    async fetchFilterOptions() {
      try {
        const headers = await this.getAuthHeader();

        const res = await axios.get(
          `${this.$apiURL}/api/vouchers/filter-options`,
          {
            headers,
          }
        );

        this.vesselOptions = res.data.vessels || [];
        this.driverOptions = res.data.drivers || [];
      } catch (err) {
        console.error("❌ fetchFilterOptions error:", err);
        this.toast.error("Failed to load vessel / driver filters");
      }
    },

    openLinkModal(voucherId) {
      this.selectedVoucherId = voucherId;
      this.linkJobId = "";
      this.showLinkModal = true;
    },

    closeLinkModal() {
      this.showLinkModal = false;
      this.selectedVoucherId = "";
      this.linkJobId = "";
    },

    async confirmLinkJob() {
      if (!this.linkJobId) {
        this.toast.error("Please enter Job ID");
        return;
      }

      try {
        const headers = await this.getAuthHeader();

        const res = await axios.put(
          `${this.$apiURL}/api/vouchers/${this.selectedVoucherId}/link-job`,
          {
            jobId: this.linkJobId,
          },
          {
            headers,
          }
        );

        if (res.data.ok) {
          this.toast.success("Voucher linked successfully!");
          this.closeLinkModal();
          await this.fetchVouchers();
        } else {
          this.toast.error(res.data.error || "Failed to link voucher");
        }
      } catch (err) {
        console.error("❌ link-job error:", err);
        this.toast.error("Error linking voucher");
      }
    },

    async fetchVouchers(loadMore = false) {
      if (!loadMore) {
        const { startDate, endDate } = this.filters;

        if (startDate && endDate && startDate > endDate) {
          this.toast.error("Start date must be on or before end date");
          return;
        }

        this.appliedFilters = { ...this.filters };
        this.vouchers = [];
        this.lastDocId = null;
        this.hasMore = true;
      }

      const { startDate, endDate, vessel, driverId } = this.appliedFilters;

      this.isLoading = true;

      try {
        const headers = await this.getAuthHeader();

        const params = {
          limit: this.pageSize,
          lastDocId: this.lastDocId,
        };

        if (startDate) {
          params.startDate = startDate;
        }

        if (endDate) {
          params.endDate = endDate;
        }

        if (vessel) {
          params.vessel = vessel;
        }

        if (driverId && this.isAdmin) {
          params.driverId = driverId;
        }

        const res = await axios.get(
          `${this.$apiURL}/api/vouchers/paginated`,
          {
            headers,
            params,
          }
        );

        const newVouchers = res.data.vouchers || [];

        if (loadMore) {
          this.vouchers.push(...newVouchers);
        } else {
          this.vouchers = newVouchers;
        }

        this.lastDocId = res.data.lastDocId;
        this.hasMore =
          typeof res.data.hasMore === "boolean"
            ? res.data.hasMore
            : newVouchers.length === this.pageSize;
      } catch (err) {
        console.error("❌ fetchVouchers error:", err);
        this.toast.error("Failed to load vouchers");
      } finally {
        this.isLoading = false;
      }
    },

    goCreateVoucher() {
      if (!this.userId) {
        this.toast.error("User ID missing");
        return;
      }

      const last6 = this.userId.slice(-6);

      this.$router.push(`/DriverNewVoucher/${last6}`);
    },

    formatDT(ts) {
      if (!ts) {
        return "—";
      }

      try {
        const d = new Date(ts);

        return d.toLocaleString("en-SG", {
          hour12: false,
        });
      } catch {
        return ts;
      }
    },
  },

  async mounted() {
    this.userId = this.$store.getters.getUserId;

    console.log(this.userId);

    dayjs.extend(utc);
    dayjs.extend(timezone);

    const sg = dayjs().tz("Asia/Singapore");
    this.selectedDate = sg.format("YYYY-MM-DD");

    const auth = getAuth();

    onAuthStateChanged(auth, async (user) => {
      if (!user) {
        this.toast.error("Please login again");
        this.$router.push("/login");
        return;
      }

      await Promise.all([
        this.fetchVouchers(),
        this.fetchFilterOptions(),
      ]);
    });
  },
};
</script>

<style scoped></style>